import type { Chapter, LocaleCode } from '@fixit/bozp-content';
import { resolveLocalized } from '@fixit/bozp-renderer';
import { useT } from '@/i18n';

export type ChapterUiState = 'done' | 'active' | 'locked';

export function ChapterSidebar({
  chapters,
  stateById,
  activeChapterId,
  quizUnlocked,
  locale,
  drawerOpen,
  onSelectChapter,
  onSelectQuiz,
  onClose,
}: {
  chapters: Chapter[];
  stateById: Record<string, ChapterUiState>;
  activeChapterId: string;
  quizUnlocked: boolean;
  locale: LocaleCode;
  drawerOpen: boolean;
  onSelectChapter: (id: string) => void;
  onSelectQuiz: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const doneCount = chapters.filter((c) => stateById[c.id] === 'done').length;

  return (
    <nav
      aria-label={t('Contents')}
      className={`fixed inset-y-0 left-0 z-50 flex w-80 max-w-[85vw] shrink-0 flex-col border-r border-line bg-paper-2 transition-transform duration-300 ease-out lg:static lg:z-auto lg:h-full lg:translate-x-0 ${
        drawerOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
      }`}
    >
      {/* sidebar masthead */}
      <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-5">
        <div className="flex items-center gap-2.5">
          <span aria-hidden className="h-4 w-4 rounded-[3px] bg-fixit-600" />
          <span className="hb-eyebrow">{t('Contents')}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('Close')}
          className="rounded-[3px] p-1 text-ink-faint hover:text-ink-strong lg:hidden"
        >
          <span aria-hidden className="text-lg leading-none">
            ✕
          </span>
        </button>
      </div>

      <div className="border-b border-line px-5 py-3 text-xs text-ink-faint">
        {doneCount} / {chapters.length} · {t('Chapter')}
      </div>

      <ol className="flex-1 overflow-y-auto p-3">
        {chapters.map((ch, i) => {
          const st = stateById[ch.id] ?? 'locked';
          const disabled = st === 'locked';
          const active = ch.id === activeChapterId;
          return (
            <li key={ch.id}>
              <button
                type="button"
                disabled={disabled}
                aria-current={active ? 'step' : undefined}
                data-testid={`chapter-${ch.id}`}
                onClick={() => !disabled && onSelectChapter(ch.id)}
                className={`group relative flex w-full items-center gap-3 rounded-[var(--hb-radius)] px-3 py-2.5 text-left transition-colors ${
                  active ? 'bg-card shadow-[inset_3px_0_0_var(--color-fixit-600)]' : ''
                } ${disabled ? 'cursor-not-allowed text-ink-faint' : 'text-ink hover:bg-card/70'}`}
              >
                <span
                  className={`hb-numeral w-7 shrink-0 text-base ${
                    disabled ? 'text-ink-faint' : ''
                  } ${st === 'done' ? '!text-ink-faint' : ''}`}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span
                  className={`min-w-0 flex-1 text-sm leading-snug ${
                    active ? 'font-semibold text-ink-strong' : ''
                  }`}
                >
                  {resolveLocalized(ch.title, locale)}
                </span>
                <span aria-hidden className="shrink-0 text-sm">
                  {st === 'done' ? (
                    <span className="text-[#2c6b39]">✓</span>
                  ) : st === 'locked' ? (
                    <span className="text-ink-faint">🔒</span>
                  ) : (
                    <span className="text-fixit-600">▸</span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <div className="border-t border-line p-3">
        <button
          type="button"
          disabled={!quizUnlocked}
          data-testid="nav-quiz"
          onClick={() => quizUnlocked && onSelectQuiz()}
          className={`flex w-full items-center gap-3 rounded-[var(--hb-radius)] px-3 py-3 text-left text-sm font-semibold transition-colors ${
            quizUnlocked
              ? 'bg-fixit-600 text-white hover:bg-fixit-700'
              : 'cursor-not-allowed bg-paper-3 text-ink-faint'
          }`}
        >
          <span aria-hidden>{quizUnlocked ? '▸' : '🔒'}</span>
          <span>{t('Final test')}</span>
        </button>
      </div>
    </nav>
  );
}
